import { Package, RegistrationSession } from "@/api/dtos";
import { DEFAULT_SESSION_GROUP, TRAINEE_AND_EARLY_CAREER_EVENT_PACKAGE_ONLY } from "@/constants/constants";
import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert } from "@mui/material";
import { useScopedField } from "@ngt/forms-core";
import { FC, useCallback, useContext, useState, useEffect } from "react";
import PackageContext from "@/contexts/PackageContext";
import RegistrationField from "@/components/RegistrationField";
import ErrorView from "@/components/form/inputs/ErrorView";
import SpeakerPackageSessions from "./SpeakerPackageSessions";
import useSessions from "@/hooks/useSessions";
import { useIsCustomRegistration } from "@/hooks/useIsCustomRegistration";
import useIsAdminSection from "@/hooks/useIsAdminSection";
import { useIsCap, useIsTAndECSpeaker } from "@/hooks/useUserRoles";

const usePackageId = () => {
    const {state: { value: packageId }, context: { setValue: setPackageId }} = useScopedField<number>("packageId", {value: true});

    return {packageId, setPackageId};
}

const usePackageChange = (isEarlyBird: boolean) => {
    const {context: { setValue: setPackageId }} = useScopedField<number>("packageId", { });
    const {context: {setValue: setRegistrationSessions}} = useScopedField<RegistrationSession[]>("sessions", { });
    //const {context: {setValue: setCost}} = useScopedField<number>("cost", { });

    return useCallback((packageInformation: Package) => {
        setPackageId(packageInformation.id, true, true, true, true);
        //setAttendeeCost(0);
        setRegistrationSessions(current => {
            const newSessions: RegistrationSession[] = [];
            
            if (packageInformation.sessions) {
                for (let session of packageInformation.sessions) {
                    if(!session.required){
                        const selectedSession = new RegistrationSession({ 
                            sessionId: session.id
                        });

                        if (session.required) {
                            (selectedSession as any).requiredGroup = session.requiredGroup  ?? DEFAULT_SESSION_GROUP;
                        }

                        newSessions.push(selectedSession);
                    }
                }
            }
            
            return newSessions;
        }, true, true, true, false);

        // if(!isEarlyBird || !packageInformation.discountPrice){
        //     setCost(packageInformation.price)
        // }
        // else{
        //     setCost(packageInformation.discountPrice)
        // }

        // window.scrollTo({
        //     top: 0,
        //     left: 0,
        //     behavior: 'smooth'
        // });
    }, [setPackageId, setRegistrationSessions, isEarlyBird])
}


const SpeakerPackagesChoice: FC = ({
}) => {
    const isAdminSection = useIsAdminSection();

    const {packageId} = usePackageId();

    const {packages, isEarlyBird, packagesLoading: loading } = useContext(PackageContext);

    const {data: allSessions, loading: allSessionsLoading} = useSessions();
    
    const onPackageChange = usePackageChange(isEarlyBird ?? false);

    const isCustomRegistration = useIsCustomRegistration();

    const {state: { value: trialCoordinator }} = useScopedField<boolean>("trialCoordinator", { value: true });
    const {state: { value: student }} = useScopedField<boolean>("student", { value: true });
    const {state: { value: registrar }} = useScopedField<boolean>("registrar", { value: true });
    const {state: { value: member }} = useScopedField<number>("member", { value: true });
    const isCap = useIsCap();
    const isTraineeGrant = useIsTAndECSpeaker();

    const [packageToUse, setPackageToUse] = useState<Package>();

    useEffect(() => {
        if (packages) {
            const packageToUse = packages.find(x => {
                if (isCustomRegistration && !isAdminSection) {
                    if (isTraineeGrant && x.id === TRAINEE_AND_EARLY_CAREER_EVENT_PACKAGE_ONLY) {
                        return true;
                    }

                    if (x.showToSpeaker && !isTraineeGrant) {
                        //if the user is a CAP member, show any packages that are for members, regardless of other restrictions, 
                        // since CAP membership is the main qualifier for those packages. i have a feeling all CAP members are BCT members,
                        // but just in case there are some edge cases, we want to make sure to show them the packages they are eligible for.
                        //cap members get free registration anyway - this is mainly just to ensure that there are at least some sessions showing for them =\
                        if (isCap &&
                            x.memberPackage) {
                            return true;
                        }
                        
                        if (
                            member && 
                            trialCoordinator && 
                            x.memberPackage && 
                            x.restrictToTc
                        )
                        {
                            return true;
                        }
                        if (
                            member && 
                            registrar && 
                            x.memberPackage && 
                            x.restrictToRegistrar
                        )
                        {
                            return true;
                        }
                        if (
                            member &&
                            student && 
                            x.memberPackage && 
                            x.restrictToStudent 
                        )
                        {
                            return true;
                        }
                        if (
                            member && 
                            !trialCoordinator && 
                            !registrar &&
                            !student && 
                            x.memberPackage && 
                            !x.restrictToTc && 
                            !x.restrictToRegistrar && 
                            !x.restrictToStudent
                        ) 
                        {
                            return true;
                        }
                        if (
                            !member && 
                            trialCoordinator &&
                            !x.memberPackage && 
                            x.restrictToTc
                        )
                        {
                            return true;
                        }
                        if (
                            !member && 
                            registrar &&
                            !x.memberPackage &&
                            x.restrictToRegistrar
                        )
                        {
                            return true;
                        }
                        if (
                            !member && 
                            student &&
                            !x.memberPackage && 
                            x.restrictToStudent
                        )
                        {
                            return true;
                        }
                        if (
                            !member && 
                            !trialCoordinator && 
                            !registrar &&
                            !student && 
                            !x.memberPackage && 
                            !x.restrictToTc && 
                            !x.restrictToRegistrar && 
                            !x.restrictToStudent
                        ) {
                            return true;
                        }

                    }
                    return false;
                }

                return true;
            });

            if (packageToUse) {
                setPackageToUse(packageToUse);
            }
        }
    }, [packages, isCustomRegistration, isAdminSection, trialCoordinator, student, registrar, member, isCap, isTraineeGrant]);


    useEffect(() => {
        //i'm not sure why we have to make sure the package is shown to speakers to auto select it for them.
        //rather than just assume that if a package has been auto selected in the useEffect above that it should
        //fire the onPackage change, I'm just changing it to say it should fire if the person is a trainee grant recipient and the package 
        // is the trainee grant package.
        const firePackageChange = packageToUse?.showToSpeaker || 
            (isTraineeGrant && packageToUse?.id === TRAINEE_AND_EARLY_CAREER_EVENT_PACKAGE_ONLY);

        if (packageToUse && isCustomRegistration && firePackageChange && !isAdminSection && (packageId !== packageToUse.id)) {
            onPackageChange(packageToUse);
        }
    }, [isCustomRegistration, isAdminSection, packageToUse, packageId, onPackageChange, isTraineeGrant]);

    if (loading || allSessionsLoading) {
        return (
            <Alert
                icon={<FontAwesomeIcon icon={faInfoCircle} />}
                severity="info"
                sx={{
                    alignItems: 'center'
                }}
            >
                Loading available packages based on your membership
            </Alert>
        )
    }
    
    return (
        <>
            {
                packageToUse && allSessions && (
                    <SpeakerPackageSessions 
                        packageInformation={packageToUse} 
                        allSessions={allSessions}
                    />
                )
            }

            <RegistrationField
                propertyPath="packageId"
                component={ErrorView}
                view="Alerts"
            />
        </>
    )
}

export default SpeakerPackagesChoice;